import { create } from 'zustand';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { logger } from 'src/services';
import { Form } from 'src/models';
import { useChatValidationStore } from '../chat/useChatValidationStore';
import { useChatStore } from '../chat';
import { useAuthStore } from '../auth';
import {
  addFormResponseApi,
  AddFormResponseApiPayload,
  getMedicationsAndAllergiesApi,
  GetMedicationsAndAllergiesApiPayload,
  getNextFormApi,
  SaveMedicationsAndAllergiesApiPayload,
  saveMedicationsAndAllergiesApi,
} from 'src/api';
import { useConfigStore } from '../settings';

type State = {
  loading: boolean;
  activeForm: Form | null;
  reviewDetails: {
    medications: string[];
    allergies: string[];
  } | null;
  error: string | null;
};

type Actions = {
  getNextForm: () => Promise<void>;
  saveForm: (payload: AddFormResponseApiPayload) => Promise<void>;
  getMedicationsAndAllergies: (
    payload: GetMedicationsAndAllergiesApiPayload
  ) => Promise<void>;
  saveMedicationsAndAllergies: (
    payload: SaveMedicationsAndAllergiesApiPayload
  ) => Promise<void>;
};

export const useFormStore = create<State & Actions>((set) => ({
  loading: false,
  activeForm: null,
  reviewDetails: null,
  error: null,
  getNextForm: async () => {
    set({ loading: true });
    try {
      const { data } = await getNextFormApi();
      const nextForm = data.next_form || null;
      set({ activeForm: nextForm });
      if (!nextForm) {
        useFormStore.getState().getMedicationsAndAllergies({
          chatToken: useChatValidationStore.getState().token!,
        });
      }
    } catch (error) {
      const message = 'Failed to fetch form';
      logger.error(message);
      set({ error: message });
    }
    set({ loading: false });
  },
  saveForm: async (payload) => {
    try {
      const { data } = await addFormResponseApi(payload);
      const nextForm = data.next_form || null;
      set({ activeForm: nextForm });
      if (!nextForm) {
        useFormStore.getState().getMedicationsAndAllergies({
          chatToken: useChatValidationStore.getState().token!,
        });
      }
    } catch (error) {
      const message = 'Failed to save form';
      toast.error(message);
      logger.error(message);
      set({ error: message });
    }
  },
  getMedicationsAndAllergies: async ({ chatToken }) => {
    const appointmentType =
      useAuthStore.getState().appointment?.appointment_type_id;
    if (
      appointmentType === 2 ||
      !useConfigStore.getState().config?.allow_medication_review
    ) {
      await useChatStore.getState().markComplete({
        chatToken,
        uuid: useAuthStore.getState().appointment?.chat_uuid!,
      });
      return;
    }

    set({ loading: true });
    try {
      const { data } = await getMedicationsAndAllergiesApi({ chatToken });
      const { medications, allergies } = data;
      set({
        reviewDetails: {
          medications: medications || [],
          allergies: allergies || [],
        },
      });
    } catch (error) {
      const message = 'Failed to fetch medications and allergies';
      toast.error(message);
      logger.error(message);
      set({ error: message });
    }
    set({ loading: false });
  },
  saveMedicationsAndAllergies: async ({
    chatToken,
    medications,
    allergies,
  }) => {
    try {
      await saveMedicationsAndAllergiesApi({
        chatToken,
        medications,
        allergies,
      });
      set({ reviewDetails: null });
      await useChatStore.getState().markComplete({
        chatToken,
        uuid: useAuthStore.getState().appointment?.chat_uuid!,
      });
    } catch (error) {
      const message = 'Failed to save medications and allergies';
      toast.error(message);
      logger.error(message);
      set({ error: message });
    }
  },
}));
